<template>
  <div class="certificate-browser">
    <h2 class="sr-only">资质认证列表</h2>

    <div v-if="pending && items.length === 0" class="certificate-state" role="status">
      <i class="i-lucide-loader-circle is-spinning" aria-hidden="true"></i>
      <span>正在加载资质证书...</span>
    </div>

    <div v-else-if="error" class="certificate-state is-error" role="alert">
      <i class="i-lucide-circle-alert" aria-hidden="true"></i>
      <strong>资质证书暂时无法加载</strong>
      <p>请稍后重试，已发布资料加载成功后将在此展示。</p>
      <button type="button" @click="emit('retry')">
        <i class="i-lucide-refresh-cw" aria-hidden="true"></i>
        重新加载
      </button>
    </div>

    <template v-else-if="items.length > 0">
      <div class="certificate-grid" role="list" aria-label="资质与荣誉证书列表">
        <article v-for="item in items" :key="item.id" class="certificate-card" role="listitem">
          <button type="button" :aria-label="`查看${item.name}详情`" @click="openPreview(item)">
            <certificate-media
              class="certificate-card-media"
              :source="item.cover_url"
              :alt="item.image_alt || item.name"
            />
            <span class="certificate-card-copy">
              <strong>{{ item.name }}</strong>
            </span>
          </button>
        </article>
      </div>

      <nav v-if="pageCount > 1" class="certificate-pagination" aria-label="资质证书分页">
        <button
          class="pagination-direction"
          type="button"
          :disabled="page <= 1 || pending"
          aria-label="上一页"
          @click="goToPage(page - 1)"
        >
          <i class="i-lucide-chevron-left" aria-hidden="true"></i>
          <span>上一页</span>
        </button>

        <template v-for="token in paginationItems" :key="token">
          <span v-if="typeof token === 'string'" class="pagination-ellipsis" aria-hidden="true">
            ···
          </span>
          <button
            v-else
            class="pagination-page"
            :class="{ active: token === page }"
            type="button"
            :disabled="pending"
            :aria-label="`第 ${token} 页`"
            :aria-current="token === page ? 'page' : undefined"
            @click="goToPage(token)"
          >
            {{ token }}
          </button>
        </template>

        <button
          class="pagination-direction"
          type="button"
          :disabled="page >= pageCount || pending"
          aria-label="下一页"
          @click="goToPage(page + 1)"
        >
          <span>下一页</span>
          <i class="i-lucide-chevron-right" aria-hidden="true"></i>
        </button>
      </nav>
    </template>

    <div v-else class="certificate-state is-empty" role="status">
      <i class="i-lucide-file-search" aria-hidden="true"></i>
      <strong>暂无公开展示的资质证书</strong>
      <p>正式资料发布后将在此展示。</p>
    </div>

    <certificate-preview :certificate="activeCertificate" @close="closePreview" />
  </div>
</template>

<script setup lang="ts">
  import type { CertificateItem } from '~/types/site-api'

  type PaginationToken = 'end-ellipsis' | 'start-ellipsis' | number

  interface Props {
    error?: boolean
    items: CertificateItem[]
    page: number
    pageCount: number
    pending?: boolean
    previewItem?: CertificateItem
  }

  defineOptions({
    name: 'CertificateGallery',
  })

  const props = withDefaults(defineProps<Props>(), {
    error: false,
    pending: false,
    previewItem: undefined,
  })

  const emit = defineEmits<{
    'close-preview': []
    retry: []
    'update:page': [page: number]
  }>()

  const selectedCertificate = shallowRef<CertificateItem>()
  const activeCertificate = computed(() => props.previewItem ?? selectedCertificate.value)

  const paginationItems = computed<PaginationToken[]>(() => {
    if (props.pageCount <= 7) {
      return Array.from({ length: props.pageCount }, (_, index) => index + 1)
    }

    const pages: PaginationToken[] = [1]
    const start = Math.max(2, props.page - 1)
    const end = Math.min(props.pageCount - 1, props.page + 1)

    if (start > 2) pages.push('start-ellipsis')
    for (let current = start; current <= end; current += 1) pages.push(current)
    if (end < props.pageCount - 1) pages.push('end-ellipsis')
    pages.push(props.pageCount)

    return pages
  })

  /** 请求有效范围内的页码。 */
  function goToPage(nextPage: number) {
    if (props.pending || nextPage === props.page || nextPage < 1 || nextPage > props.pageCount)
      return
    emit('update:page', nextPage)
  }

  /** 打开证书大图与公开信息预览。 */
  function openPreview(item: CertificateItem) {
    selectedCertificate.value = item
  }

  /** 关闭本地或由 URL 打开的证书预览。 */
  function closePreview() {
    selectedCertificate.value = undefined
    emit('close-preview')
  }
</script>

<style scoped lang="scss">
  .certificate-browser {
    min-width: 0;
  }

  .certificate-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 26px;
  }

  .certificate-card {
    min-width: 0;
  }

  .certificate-card > button {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 0;
    overflow: hidden;
    color: var(--ink);
    text-align: left;
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    box-shadow: 0 8px 24px rgb(55 33 36 / 4%);
    transition:
      border-color 220ms var(--ease),
      box-shadow 220ms var(--ease),
      transform 220ms var(--ease);
  }

  .certificate-card > button:hover {
    border-color: #d5b7ba;
    box-shadow: 0 18px 38px rgb(72 31 35 / 10%);
    transform: translateY(-4px);
  }

  .certificate-card > button:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
  }

  .certificate-card-media {
    aspect-ratio: 4 / 3;
    padding: 18px;
    border-bottom: 1px solid #e5e7e9;
  }

  .certificate-card-copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    min-height: 88px;
    padding: 18px 20px;
  }

  .certificate-card-copy strong {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 17px;
    font-weight: 620;
    line-height: 1.5;
    -webkit-box-orient: vertical;
  }

  .certificate-state {
    display: grid;
    gap: 12px;
    place-items: center;
    min-height: 360px;
    padding: 48px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
  }

  .certificate-state > i {
    width: 34px;
    height: 34px;
    color: var(--accent);
  }

  .certificate-state strong {
    font-size: 19px;
    color: var(--ink);
  }

  .certificate-state p {
    max-width: 440px;
    margin: -4px 0 0;
    font-size: 13px;
  }

  .certificate-state button {
    display: inline-flex;
    gap: 7px;
    align-items: center;
    min-height: 40px;
    padding: 0 16px;
    color: var(--white);
    cursor: pointer;
    background: var(--accent);
    border: 0;
    border-radius: var(--radius-md);
  }

  .certificate-state button i {
    width: 15px;
    height: 15px;
  }

  .is-spinning {
    animation: certificate-spin 900ms linear infinite;
  }

  .certificate-pagination {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: center;
    margin-top: 42px;
  }

  .certificate-pagination button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 42px;
    height: 42px;
    color: var(--muted);
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    transition:
      color 180ms var(--ease),
      background 180ms var(--ease),
      border-color 180ms var(--ease);
  }

  .certificate-pagination button:hover:not(:disabled),
  .certificate-pagination .pagination-page.active {
    color: var(--white);
    background: var(--accent);
    border-color: var(--accent);
  }

  .certificate-pagination button:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .certificate-pagination button:disabled {
    cursor: not-allowed;
    opacity: 0.42;
  }

  .pagination-direction {
    gap: 5px;
    padding-inline: 13px;
  }

  .pagination-direction i {
    width: 15px;
    height: 15px;
  }

  .pagination-ellipsis {
    min-width: 24px;
    color: var(--muted);
    text-align: center;
  }

  @keyframes certificate-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 1023px) {
    .certificate-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 20px;
    }
  }

  @media (max-width: 767px) {
    .certificate-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
    }

    .certificate-card-media {
      padding: 10px;
    }

    .certificate-card-copy {
      min-height: 82px;
      padding: 14px;
    }

    .certificate-card-copy strong {
      font-size: 15px;
    }

    .certificate-state {
      min-height: 300px;
    }

    .certificate-pagination {
      gap: 6px;
      margin-top: 32px;
    }

    .certificate-pagination button {
      min-width: 38px;
      height: 38px;
    }

    .pagination-direction {
      padding-inline: 10px;
    }

    .pagination-direction span {
      display: none;
    }
  }

  @media (max-width: 420px) {
    .certificate-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .certificate-card-media {
      aspect-ratio: 4 / 3;
      padding: 18px;
    }

    .certificate-card-copy {
      min-height: 82px;
      padding: 16px 18px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .certificate-card > button {
      transition: none;
    }

    .certificate-card > button:hover {
      transform: none;
    }

    .is-spinning {
      animation: none;
    }
  }
</style>
